import React from 'react';
import { Box, IconButton, Select, MenuItem, FormControl, Tooltip, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Replay10Icon from '@mui/icons-material/Replay10';
import Forward10Icon from '@mui/icons-material/Forward10';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import FastForwardIcon from '@mui/icons-material/FastForward';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DownloadIcon from '@mui/icons-material/Download';

const PLAYBACK_SPEEDS = [0.5, 1, 2, 4];

function formatTime(seconds) {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function PlaybackControls({
  isPlaying,
  currentTime,
  duration,
  playbackSpeed,
  onPlayPause,
  onSeek,
  onSpeedChange,
  onSnapshot,
  onDownload,
  disabled = false,
}) {
  const handleSkip = (seconds) => {
    if (onSeek) {
      const newTime = Math.max(0, Math.min(duration || 0, (currentTime || 0) + seconds));
      onSeek(newTime);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        bgcolor: 'background.paper',
        borderRadius: 1,
      }}
    >
      {/* Skip back 1 minute */}
      <Tooltip title="Back 1 minute">
        <span>
          <IconButton
            size="small"
            onClick={() => handleSkip(-60)}
            disabled={disabled}
          >
            <FastRewindIcon />
          </IconButton>
        </span>
      </Tooltip>

      {/* Skip back 10 seconds */}
      <Tooltip title="Back 10 seconds">
        <span>
          <IconButton
            size="small"
            onClick={() => handleSkip(-10)}
            disabled={disabled}
          >
            <Replay10Icon />
          </IconButton>
        </span>
      </Tooltip>

      {/* Play/Pause */}
      <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
        <span>
          <IconButton
            onClick={onPlayPause}
            disabled={disabled}
            sx={{ bgcolor: 'primary.main', color: 'common.white', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </span>
      </Tooltip>

      {/* Skip forward 10 seconds */}
      <Tooltip title="Forward 10 seconds">
        <span>
          <IconButton
            size="small"
            onClick={() => handleSkip(10)}
            disabled={disabled}
          >
            <Forward10Icon />
          </IconButton>
        </span>
      </Tooltip>

      {/* Skip forward 1 minute */}
      <Tooltip title="Forward 1 minute">
        <span>
          <IconButton
            size="small"
            onClick={() => handleSkip(60)}
            disabled={disabled}
          >
            <FastForwardIcon />
          </IconButton>
        </span>
      </Tooltip>

      {/* Time display */}
      <Typography variant="body2" sx={{ mx: 1, minWidth: 100, textAlign: 'center' }}>
        {formatTime(currentTime)} / {formatTime(duration)}
      </Typography>

      {/* Speed selector */}
      <FormControl size="small" sx={{ minWidth: 80 }}>
        <Select
          value={playbackSpeed || 1}
          onChange={(e) => onSpeedChange?.(e.target.value)}
          disabled={disabled}
          size="small"
        >
          {PLAYBACK_SPEEDS.map((speed) => (
            <MenuItem key={speed} value={speed}>
              {speed}x
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ flexGrow: 1 }} />

      {/* Snapshot button */}
      <Tooltip title="Take snapshot">
        <span>
          <IconButton
            onClick={onSnapshot}
            disabled={disabled}
          >
            <CameraAltIcon />
          </IconButton>
        </span>
      </Tooltip>

      {/* Download button */}
      <Tooltip title="Download recording">
        <span>
          <IconButton
            onClick={onDownload}
            disabled={disabled}
          >
            <DownloadIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

export default PlaybackControls;
